export type WidgetRepresentation =
  | 'number'
  | 'meter'
  | 'bar'
  | 'line'
  | 'donut'
  | 'pie'
  | 'radar'
  | 'list'
  | 'org'
  /** Headline value + a small bar (e.g. team attendance today + this week). */
  | 'stat-chart'
  /** One donut ring per leave type — a leave summary. */
  | 'leave-summary'
  /** Several metrics in one card (e.g. headcount + open vacancies). */
  | 'multi-stat'
  | 'clock';

/** One leave type's usage, drawn as a donut ring in the leave-summary card. */
export interface LeaveUsage {
  type: string;
  used: number;
  total: number;
}

/**
 * A node in a reporting hierarchy (manager → you → your reports). NOT the
 * org-structure tree — this is people and who-reports-to-whom.
 */
export interface OrgNode {
  name: string;
  /** Job title / role line under the name. */
  title?: string;
  /** Profile picture URL; the tile falls back to initials when absent. */
  avatar?: string;
  /** The logged-in user's own node — highlighted in the chart. */
  self?: boolean;
  children?: OrgNode[];
}

/**
 * One row in an action-list widget — a thing waiting on the user, linking to the
 * page that clears it. `tone` flags urgency (shown as a coloured tag + text,
 * never colour alone).
 */
export interface WidgetListItem {
  label: string;
  sublabel?: string;
  /** Short badge, e.g. a count or "Now". */
  tag?: string;
  tone?: 'urgent' | 'info' | 'neutral';
  /** PrimeIcons class for the leading icon. */
  icon?: string;
  /** Route the row drills into. */
  link?: string;
}

export interface WidgetDescriptor {
  /** Stable id, also the user-preference key. */
  id: string;
  title: string;
  /** Resolved by the data registry, never a service reference. */
  dataSourceId: string;

  representation: {
    /** The active representation (user override wins over this default). */
    type: WidgetRepresentation;
    /** The curated menu of representations valid for THIS widget's data. */
    options: WidgetRepresentation[];
  };

  /** Optional one-line context under a chart's title. */
  subtitle?: string;
  /** For 'stat-chart' cards: the inner chart's default style. */
  chartStyle?: WidgetRepresentation;
  /** For 'stat-chart' cards: styles the user can switch the inner chart to. */
  chartStyleOptions?: WidgetRepresentation[];
  /** PrimeIcons class shown on the tile. Neutral — carries no state. */
  icon?: string;
  /** Icon-box accent colour name (e.g. 'blue', 'violet', 'emerald'). Cosmetic. */
  accent?: string;
  /** Route the widget drills into. */
  link?: string;
  /** Scope gate — reuses PermissionService, same as nav entries. */
  permission?: string;

  /** Rows to show in a click-through overlay (e.g. absenteeism → leave types). */
  overlayItems?: WidgetListItem[];

  layout: {
    /** sm = one KPI tile; md = half-width chart; lg = full-width chart. */
    size: 'sm' | 'md' | 'lg';
    /**
     * Dashboard zone. 'tiles' = the top KPI row; 'left' = the cards column;
     * 'right' = the side column (reporting line), stretched to the left's height;
     * 'full' = a full-width row below the two columns.
     */
    region?: 'tiles' | 'left' | 'right' | 'full';
    /** Ascending; the grid sorts by this. */
    order: number;
  };
}

/**
 * A trend chip on a stat tile ("↓ 8.4% attrition"). `sentiment` — NOT direction —
 * decides colour: a rising cost is bad, a rising headcount is good, so the arrow
 * and the colour are independent.
 */
export interface WidgetDelta {
  /** Preformatted, e.g. "8.4%" or "1,840 days". */
  value: string;
  direction: 'up' | 'down';
  sentiment?: 'good' | 'bad' | 'neutral';
  /** Optional trailing word, e.g. "attrition", "this month". */
  label?: string;
}

/**
 * A companion count folded into a stat tile — same card, not a divided column
 * (e.g. Total Headcount's card also naming Open Vacancies).
 */
export interface WidgetSecondaryStat {
  label: string;
  value: number | string;
  icon?: string;
  link?: string;
}

/** One metric inside a 'multi-stat' card (e.g. Headcount, then Open Vacancies). */
export interface MultiStat {
  label: string;
  value: number | string;
  unit?: string;
  icon?: string;
  /** Icon-box accent colour name. */
  accent?: string;
  delta?: WidgetDelta;
  /** Route this metric drills into. */
  link?: string;
}

/** One plotted series. Colour is assigned by the representation, not carried here. */
export interface WidgetSeries {
  label?: string;
  data: number[];
  /** 'bar' or 'line' when a chart mixes them (e.g. actual bars + forecast line). */
  kind?: 'bar' | 'line';
  /** Draw as a dashed line — used to mark the forecast tail. */
  dashed?: boolean;
}

/** Multi-series chart payload consumed by bar/line/donut/radar representations. */
export interface WidgetChart {
  labels: string[];
  datasets: WidgetSeries[];
}

/**
 * The normalized data every representation consumes. A data source produces
 * this; a representation reads only the fields it needs (number → `value` (+
 * `delta`), meter → `value`+`max`, bar/line/donut/radar → `chart`).
 *
 * `loading`/`error` let each widget degrade independently — one dead source
 * shows a dash, never blanks the grid.
 */
export interface WidgetData {
  value: number | string;
  /** Denominator for a meter/ratio (e.g. 21 working days, 25 leave days). */
  max?: number;
  unit?: string;
  /** Trend chip for a stat tile. */
  delta?: WidgetDelta;
  /** A second count folded into the same stat tile (not a divided card). */
  secondary?: WidgetSecondaryStat;
  /** Series payload for chart representations. */
  chart?: WidgetChart;
  /** Rows for a list representation (action list). */
  items?: WidgetListItem[];
  /** Root of a reporting hierarchy for the 'org' representation. */
  org?: OrgNode;
  /** Per-type usage for the 'leave-summary' representation. */
  leaveSummary?: LeaveUsage[];
  /** Several metrics for the 'multi-stat' representation. */
  stats?: MultiStat[];
  loading: boolean;
  error?: boolean;
  attendanceToday?: AttendanceToday;
  birthdays?: BirthdayEmployee[];
}

/** '—' is the honest empty value — never a zero that reads as real data. */
export const EMPTY_VALUE = '—';

export const loadingData = (): WidgetData => ({ value: EMPTY_VALUE, loading: true });
export const errorData = (): WidgetData => ({ value: EMPTY_VALUE, loading: false, error: true });

export interface AttendanceToday {
  status: 'NOT_STARTED' | 'IN' | 'OUT';

  shiftStart: string;
  shiftEnd: string;

  clockIn?: Date | null;
  clockOut?: Date | null;

  totalWorked: string;
}

export interface BirthdayEmployee {
  id: string;
  name: string;
  designation: string;
  avatar?: string;
  birthday: string;
}

